/* eslint-disable no-unused-vars,no-underscore-dangle */
import { PieConfig } from '@ant-design/plots'
import { CSSProperties } from 'react'

export const renderStatistic = (
	container: HTMLElement,
	containerWidth: number,
	text: string,
	style: CSSProperties
) => {
	const canvas = container.parentElement?.querySelector('canvas')
	const ctx = canvas!.getContext('2d')!
	const fontStyles = {
		fontStyle: 'normal',
		fontVariant: 'normal',
		fontWeight: 'normal',
		fontSize: '20px',
		lineHeight: '21.4px',
		...style,
	}
	ctx.font = [
		fontStyles.fontStyle,
		fontStyles.fontVariant,
		fontStyles.fontWeight,
		`${fontStyles.fontSize}/${fontStyles.lineHeight} ${
			fontStyles.fontFamily ?? 'sans-serif'
		}`,
	].join(' ')
	const { width: textWidth } = ctx.measureText(text)
	const textHeight = 20
	const R = containerWidth / 2 // r^2 = (w / 2)^2 + (h - offsetY)^2

	let scale = 1

	if (containerWidth < textWidth) {
		scale = Math.min(
			Math.sqrt(Math.abs(R ** 2 / ((textWidth / 2) ** 2 + textHeight ** 2))),
			1
		)
	}

	const textStyleStr = `width:${containerWidth}px;`
	return `<div style="${textStyleStr};font-size:${scale}em;line-height:${
		scale < 1 ? 1 : 'inherit'
	};">${text}</div>`
}

export const pieConfigBuilder: PieConfig = {
	appendPadding: 10,
	angleField: 'value',
	data: [],
	colorField: 'type',
	radius: 1,
	innerRadius: 0.7,
	// meta: {
	// 	value: {
	// 		formatter: v => `${v} ¥`,
	// 	},
	// },
	label: {
		type: 'inner',
		offset: '-50%',
		style: {
			textAlign: 'center',
		},
		autoRotate: false,
		content: '{value}',
	},
	statistic: {
		title: {
			offsetY: -4,
			customHtml: (container: HTMLElement, _: any, datum: any) => {
				const { width, height } = container.getBoundingClientRect()
				const d = Math.sqrt((width / 2) ** 2 + (height / 2) ** 2)
				const text = datum ? datum.type : 'Total'
				return renderStatistic(container, d, text, {
					fontSize: 28,
				})
			},
		},
		content: {
			offsetY: 4,
			style: {
				fontSize: '32px',
			},
			customHtml: (container: HTMLElement, _: any, datum: any, data) => {
				const { width } = container.getBoundingClientRect()
				const text = datum
					? `${datum.value}`
					: `${data?.reduce((r, d) => r + d.value, 0) ?? 0}`
				return renderStatistic(container, width, text, {
					fontSize: 32,
				})
			},
		},
	},
	interactions: [
		{
			type: 'element-selected',
		},
		{
			type: 'element-active',
		},
		{
			type: 'pie-statistic-active',
		},
	],
}

interface DatumType<T> {
	type: string
	value: T
}

interface BeautifulPieConfigProps<ValueType> {
	padding?: number
	radius?: number
	innerRadius?: number
	metaFormatter?: (_: string) => string
	label?: PieConfig['label']
	labelFormatter?: () => string
	titleHint?: string
	titleCss?: CSSProperties
	contentHint?: (_: Array<DatumType<ValueType>>) => string
	contentCss?: CSSProperties
	contentFormatter?: (_: ValueType) => string
	interactions?: PieConfig['interactions']
	originConfig?: Partial<PieConfig>
}

const beautifulPieConfig = <T>({
	padding: appendPadding,
	metaFormatter,
	labelFormatter,
	titleHint,
	titleCss,
	contentHint,
	contentCss,
	contentFormatter,
	originConfig,
	...origin
}: BeautifulPieConfigProps<T>) => {
	return {
		_data: [] as Array<DatumType<T>>,
		_config: {
			...pieConfigBuilder,
			appendPadding,
			...origin,
			...originConfig,
		} as PieConfig,
		build() {
			const config: any = {
				...this._config,
				data: this._data,
			}

			if (metaFormatter && config.meta) {
				config.meta.value.formatter = metaFormatter
			}

			if (labelFormatter && config.label) {
				config.label.content = labelFormatter
			}

			const titleCustomHTML = (container: HTMLElement, view: any, datum: any) => {
				const { width, height } = container.getBoundingClientRect()
				const d = Math.sqrt((width / 2) ** 2 + (height / 2) ** 2)
				const text = datum ? datum.type : titleHint ?? 'Total'
				return renderStatistic(container, d, text, {
					fontSize: 28,
					...titleCss,
				})
			}

			const contentCustomHTML = (
				container: HTMLElement,
				view: any,
				datum: any,
				data: Array<DatumType<T>>
			) => {
				const { width } = container.getBoundingClientRect()
				// eslint-disable-next-line no-nested-ternary
				const text = datum
					? contentFormatter
						? contentFormatter(datum.value)
						: datum.value
					: contentHint
					? contentHint(data)
					: 'CONTENT_HINT_HERE'
				return renderStatistic(container, width, text, {
					fontSize: 32,
					...contentCss,
				})
			}

			if (config?.statistic?.title) {
				config.statistic = {
					...config.statistic,
					title: {
						...config.statistic.title,
						customHtml: titleCustomHTML,
					},
				}
			}

			if (config?.statistic?.content) {
				config.statistic = {
					...config.statistic,
					content: {
						...config.statistic.title,
						customHtml: contentCustomHTML as any,
					},
				}
			}

			return config
		},
		data(items: Array<DatumType<any>>) {
			this._data = items
			return this
		},
		origin(config: Partial<PieConfig>) {
			this._config = {
				...this._config,
				...config,
			}
			return this
		},
		size({ width, height }: { width?: number; height?: number }) {
			this._config = {
				...this._config,
				height,
				width,
			}
			return this
		},
	}
}

export default beautifulPieConfig
